import {
  ref,
  uploadBytes,
  getBytes,
  getDownloadURL,
  getMetadata,
  updateMetadata as firebaseUpdateMetadata,
  deleteObject,
  UploadMetadata,
  FullMetadata
} from 'firebase/storage';
import { CoreFirebaseService } from './CoreFirebaseService';
import { extractVideoThumbnail } from '../utils/videoThumbnail';

/**
 * Response of POST /api/assets/upload-url (builder-service AssetsController).
 * `headers` on each target are SIGNED — they must be sent verbatim on the PUT
 * or GCS rejects the request.
 */
interface SignedVideoUploadTarget {
  video: { uploadUrl: string; headers: Record<string, string> };
  thumb: { uploadUrl: string; headers: Record<string, string> } | null;
  assetUrl: AssetUrl;
  thumbnailUrl: string;
}

/**
 * PUT a blob to a signed GCS URL via XHR (fetch exposes no upload progress).
 */
function putToSignedUrl(
  uploadUrl: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Storage upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Storage upload failed: network error'));
    xhr.onabort = () => reject(new Error('Storage upload aborted'));
    xhr.send(body);
  });
}

/**
 * AssetUrl structure matching @segmently/schema-models
 * Contains URLs for different image/video resolutions
 */
export interface AssetUrl {
  /** Thumbnail size */
  thumb: string;
  /** Small size (1/3 of original for images, thumbnail for videos) */
  small: string;
  /** Medium size */
  medium: string;
  /** Normal size */
  normal: string;
  /** Original file */
  origin: string;
}

/**
 * Custom metadata interface for type safety
 */
export interface StorageMetadata {
  name: string;
  fullPath: string;
  bucket: string;
  generation: string;
  metageneration: string;
  size: number;
  timeCreated: string;
  updated: string;
  contentType?: string;
  contentLanguage?: string;
  contentEncoding?: string;
  contentDisposition?: string;
  cacheControl?: string;
  customMetadata?: Record<string, string>;
  publicUrl?: string;
}

/**
 * Metadata update options
 */
export interface StorageMetadataUpdate {
  contentType?: string;
  contentLanguage?: string;
  contentEncoding?: string;
  contentDisposition?: string;
  cacheControl?: string;
  customMetadata?: Record<string, string>;
}

/**
 * StorageService - Type-safe Firebase Storage operations
 *
 * Provides upload, download, metadata, and delete operations for Firebase Storage.
 * Uses CoreFirebaseService for storage instance access.
 *
 * Example usage:
 * ```typescript
 * const storageService = new StorageService();
 *
 * // Upload file
 * const data = new Uint8Array([1, 2, 3]);
 * const metadata = await storageService.upload('uploads/file.bin', data);
 *
 * // Download file
 * const downloaded = await storageService.download('uploads/file.bin');
 *
 * // Get metadata
 * const meta = await storageService.getMetadata('uploads/file.bin');
 *
 * // Update metadata
 * const updated = await storageService.updateMetadata('uploads/file.bin', {
 *   contentType: 'application/octet-stream'
 * });
 *
 * // Get download URL
 * const url = await storageService.getDownloadURL('uploads/file.bin');
 *
 * // Delete file
 * await storageService.delete('uploads/file.bin');
 * ```
 */
export class StorageService {
  private storage = CoreFirebaseService.getInstance().storage;

  /**
   * Upload file to Firebase Storage
   *
   * @param path - Storage path for the file
   * @param data - File data as Uint8Array, Blob, or File
   * @param metadata - Optional upload metadata (content type, cache control, etc.)
   * @returns Promise resolving to file metadata
   */
  async upload(
    path: string,
    data: Uint8Array | Blob | File,
    metadata?: UploadMetadata
  ): Promise<StorageMetadata> {
    const storageRef = ref(this.storage, path);
    const uploadResult = await uploadBytes(storageRef, data, metadata);
    return this.convertMetadata(uploadResult.metadata);
  }

  /**
   * Download file from Firebase Storage
   *
   * @param path - Storage path of the file
   * @returns Promise resolving to file data as Uint8Array
   */
  async download(path: string): Promise<Uint8Array> {
    const storageRef = ref(this.storage, path);
    const arrayBuffer = await getBytes(storageRef);
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Get file metadata
   *
   * @param path - Storage path of the file
   * @returns Promise resolving to file metadata
   */
  async getMetadata(path: string): Promise<StorageMetadata> {
    const storageRef = ref(this.storage, path);
    const metadata = await getMetadata(storageRef);
    return this.convertMetadata(metadata);
  }

  /**
   * Update file metadata
   *
   * @param path - Storage path of the file
   * @param metadata - Metadata fields to update
   * @returns Promise resolving to updated metadata
   */
  async updateMetadata(
    path: string,
    metadata: StorageMetadataUpdate
  ): Promise<StorageMetadata> {
    const storageRef = ref(this.storage, path);
    const updated = await firebaseUpdateMetadata(storageRef, metadata);
    return this.convertMetadata(updated);
  }

  /**
   * Get public download URL for file
   *
   * @param path - Storage path of the file
   * @returns Promise resolving to HTTPS download URL
   */
  async getDownloadURL(path: string): Promise<string> {
    const storageRef = ref(this.storage, path);
    return getDownloadURL(storageRef);
  }

  /**
   * Get public CDN URL for file (faster, cached)
   *
   * @param path - Storage path of the file
   * @returns CDN URL string (e.g., https://api.segmently.ai/assets/path/to/file)
   */
  getPublicUrl(path: string): string {
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';
    return `${baseUrl}/assets/${cleanPath}`;
  }

  // ============================================================================
  // CDN Bucket Operations (via builder-service)
  // ============================================================================

  /**
   * Upload image to CDN bucket with automatic resizing (origin + small = 1/3)
   *
   * This method uploads images to the public CDN bucket (segmently-assets-{env})
   * via builder-service which automatically creates two versions:
   * - origin: Original image unchanged
   * - small: 1/3 of original size (proportionally)
   *
   * @param path - Base path for uploads (e.g., 'onboardings/123/images/en-US_123_logo')
   * @param file - Image file to upload
   * @returns Promise resolving to AssetUrl with different URLs for each resolution
   *
   * @example
   * ```typescript
   * const storageService = new StorageService();
   * const file = inputElement.files[0];
   * const result = await storageService.uploadImage(
   *   `onboardings/${onboardingId}/images/en-US_123_logo`,
   *   file
   * );
   * // result.assetUrl.origin → https://api.segmently.ai/assets/.../origin.png
   * // result.assetUrl.small → https://api.segmently.ai/assets/.../small.png
   * ```
   */
  async uploadImage(path: string, file: File): Promise<{ assetUrl: AssetUrl }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);

    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';
    const response = await fetch(`${baseUrl}/api/assets/upload/image`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Image upload failed');
    }

    return response.json();
  }

  /**
   * Upload video to CDN bucket with automatic thumbnail extraction
   *
   * This method uploads videos to the public CDN bucket (segmently-assets-{env})
   * via builder-service which automatically extracts a thumbnail from the first frame.
   *
   * @param path - Base path for uploads (e.g., 'onboardings/123/videos/en-US_123_intro')
   * @param file - Video file to upload
   * @returns Promise resolving to AssetUrl + thumbnailUrl
   *
   * @example
   * ```typescript
   * const storageService = new StorageService();
   * const file = inputElement.files[0];
   * const result = await storageService.uploadVideo(
   *   `onboardings/${onboardingId}/videos/en-US_123_intro`,
   *   file
   * );
   * // result.assetUrl.origin → https://api.segmently.ai/assets/.../video.mp4
   * // result.assetUrl.thumb → https://api.segmently.ai/assets/.../thumb.png
   * // result.thumbnailUrl → https://api.segmently.ai/assets/.../thumb.png
   * ```
   */
  async uploadVideo(path: string, file: File): Promise<{ assetUrl: AssetUrl; thumbnailUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);

    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/api/assets/upload/video`, {
        method: 'POST',
        body: formData,
      });
    } catch {
      // fetch rejects ("TypeError: Load failed") when the response carries no
      // CORS headers — most commonly the Cloud Run infrastructure 413 for a
      // body over 32 MiB. Surface something actionable instead.
      throw new Error(
        'Video upload failed before reaching the server — the file is likely too large for this path'
      );
    }

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || `Video upload failed (${response.status})`);
    }

    return response.json();
  }

  /**
   * Upload a large video DIRECTLY to the CDN bucket via V4 signed URLs.
   *
   * Bypasses the Cloud Run 32 MiB request cap that kills large multipart
   * uploads: builder-service only issues signed PUT URLs (member-auth
   * required), then the browser PUTs the file straight into GCS. The first
   * frame is extracted client-side (canvas) and uploaded as the thumbnail;
   * if the browser cannot decode the codec, the AssetUrl thumb fields fall
   * back to the video URL (decided server-side).
   *
   * Throws Error with `.code === 'signing_unavailable'` when the backend
   * cannot sign (local dev without impersonated ADC) — callers fall back to
   * the multipart path.
   *
   * @param path - Base path (same contract as uploadVideo)
   * @param file - Video file
   * @param options.projectId - Required for member auth on the signing endpoint
   * @param options.onProgress - Upload progress callback (0..1)
   * @returns Same shape as uploadVideo: AssetUrl + thumbnailUrl
   */
  async uploadVideoDirect(
    path: string,
    file: File,
    options: { projectId: string; onProgress?: (fraction: number) => void }
  ): Promise<{ assetUrl: AssetUrl; thumbnailUrl: string }> {
    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';

    // 1. First frame — extracted while the file is still local. Never throws.
    const thumbnail = await extractVideoThumbnail(file);

    // 2. Member auth (pattern: FontUploadService.getAuthToken).
    const token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Sign in to upload videos');
    }

    // 3. Ask builder-service for signed PUT targets.
    const response = await fetch(`${baseUrl}/api/assets/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        projectId: options.projectId,
        path,
        contentType: file.type,
        size: file.size,
        hasThumbnail: Boolean(thumbnail),
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      const error = new Error(body?.error || `Failed to get upload URL (${response.status})`);
      (error as Error & { code?: string }).code = body?.error;
      throw error;
    }

    const target = await response.json() as SignedVideoUploadTarget;

    // 4. PUT the video straight into GCS (all signed headers verbatim).
    await putToSignedUrl(target.video.uploadUrl, file, target.video.headers, options.onProgress);

    // 5. PUT the thumbnail (small; no progress reporting needed).
    if (thumbnail && target.thumb) {
      await putToSignedUrl(target.thumb.uploadUrl, thumbnail, target.thumb.headers);
    }

    return { assetUrl: target.assetUrl, thumbnailUrl: target.thumbnailUrl };
  }

  /**
   * Delete image from CDN bucket via builder-service
   *
   * @param path - Path in assets bucket to delete
   * @returns Promise that resolves when file is deleted
   *
   * @example
   * ```typescript
   * const storageService = new StorageService();
   * await storageService.deleteFromAssets('projects/123/images/old-logo.png', '123');
   * ```
   */
  async deleteFromAssets(path: string, projectId: string): Promise<void> {
    const baseUrl = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';
    if (!projectId) throw new Error('projectId is required to delete assets');
    const token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken();
    if (!token) throw new Error('Sign in to delete assets');
    const response = await fetch(`${baseUrl}/api/assets/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ path, projectId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Asset delete failed');
    }
  }

  // ============================================================================
  // Firebase Storage Operations
  // ============================================================================

  /**
   * Delete file from Firebase Storage
   *
   * @param path - Storage path of the file
   * @returns Promise that resolves when file is deleted
   */
  async delete(path: string): Promise<void> {
    const storageRef = ref(this.storage, path);
    await deleteObject(storageRef);
  }

  /**
   * Convert Firebase FullMetadata to type-safe StorageMetadata
   *
   * @param metadata - Firebase metadata object
   * @returns Type-safe metadata object
   */
  private convertMetadata(metadata: FullMetadata): StorageMetadata {
    return {
      name: metadata.name,
      fullPath: metadata.fullPath,
      bucket: metadata.bucket,
      generation: metadata.generation,
      metageneration: metadata.metageneration,
      size: metadata.size,
      timeCreated: metadata.timeCreated,
      updated: metadata.updated,
      contentType: metadata.contentType,
      contentLanguage: metadata.contentLanguage,
      contentEncoding: metadata.contentEncoding,
      contentDisposition: metadata.contentDisposition,
      cacheControl: metadata.cacheControl,
      customMetadata: metadata.customMetadata,
      publicUrl: this.getPublicUrl(metadata.fullPath)
    };
  }
}
